<div class="cgv-container">
  <h1>Politique de Confidentialité – Service Qaryos</h1>
  <p class="last-updated">Dernière mise à jour&nbsp;: 13 juin 2025</p>

  <nav class="cgv-toc">
    <h2>Sommaire</h2>
    <ul>
      <li>1. Préambule</li>
      <li>2. Données collectées</li>
      <li>3. Finalités du traitement</li>
      <li>4. Base légale du traitement</li>
      <li>5. Durée de conservation des données</li>
      <li>6. Destinataires des données et transferts hors UE</li>
      <li>7. Sécurité des données</li>
      <li>8. Droits des utilisateurs</li>
      <li>9. Cookies et outils de suivi</li>
      <li>10. Évolution de la politique de confidentialité</li>
    </ul>
  </nav>

  <section id="preambule">
    <h2>1. Préambule</h2>
    <p>
      Qaryos attache une grande importance à la protection de la vie privée de ses utilisateurs et
      s’engage à traiter les données personnelles conformément au Règlement (UE) 2016/679 dit RGPD
      et à la loi française applicable.<br />
      La présente Politique de Confidentialité décrit quelles données personnelles sont collectées
      lors de l’utilisation du site <i>www.Qaryos.fr</i> et de l’application mobile Qaryos (ci-après
      ensemble le « Service »), comment elles sont utilisées, conservées et protégées, ainsi que les
      droits des utilisateurs à cet égard.<br />
      Qaryos est édité par la société éditrice du Service (ci-après « la Société »), agissant en
      qualité de responsable du traitement. Les coordonnées complètes de la Société figurent dans
      les Mentions Légales du site. Pour toute question relative aux données personnelles, la
      Société peut être contactée à l’adresse <strong>contact&#64;Qaryos.fr</strong>.
    </p>
  </section>

  <section id="donnees-collectees">
    <h2>2. Données collectées</h2>
    <p>
      Qaryos est susceptible de collecter et traiter les catégories de données personnelles
      suivantes :
    </p>
    <ul>
      <li>
        <b>Données d’identification et de contact&nbsp;:</b> informations telles que le nom, le
        prénom, l’adresse e-mail, le nom de l’entreprise ou de l’établissement, fournis notamment
        lors du remplissage du formulaire de contact ou lors de la création d’un compte utilisateur
        sur l’application.
      </li>
      <li>
        <b>Contenu du message et des demandes&nbsp;:</b> renseignements saisis dans le
        <i>formulaire de contact</i>, notamment le sujet de la demande et le texte du message, ainsi
        que toute correspondance ultérieure.
      </li>
      <li>
        <b>Données de compte utilisateur&nbsp;:</b> informations liées au compte créé via
        l’application Qaryos (par exemple, rôle de l’utilisateur dans le Service, identifiants de
        connexion tels que l’adresse e-mail et mot de passe chiffré, historiques d’utilisation de
        l’application, etc.).
      </li>
      <li>
        <b>Données de navigation et d’utilisation&nbsp;:</b> données techniques collectées
        automatiquement lors de l’utilisation du site web ou de l’application, telles que l’adresse
        IP, le type de navigateur ou d’appareil, les pages consultées, la date et l’heure de
        connexion, ainsi que des journaux d’événements (logs) relatifs à l’utilisation du Service
        (ex&nbsp;: actions effectuées, erreurs rencontrées).
      </li>
      <li>
        <b>Cookies et données analytiques&nbsp;:</b> le site web utilise des cookies et technologies
        similaires susceptibles de collecter des informations sur la navigation de l’utilisateur
        (voir section « Cookies et outils de suivi » ci-dessous, notamment pour l’éventuelle
        utilisation de Google Analytics).
      </li>
    </ul>
    <p>
      Le caractère obligatoire ou facultatif des données collectées est généralement indiqué lors de
      la saisie. En l’absence de fourniture des informations obligatoires (par exemple pour le
      formulaire de contact ou la création de compte), la demande de l’utilisateur pourrait ne pas
      être traitée ou l’accès au Service pourrait ne pas être possible.
    </p>
  </section>

  <section id="finalites">
    <h2>3. Finalités du traitement</h2>
    <p>
      Les données personnelles collectées par Qaryos sont utilisées pour les finalités suivantes :
    </p>
    <ul>
      <li>
        Fournir et gérer le Service Qaryos, notamment la création et la gestion des comptes
        utilisateurs, l’authentification des utilisateurs, l’exécution des fonctionnalités de
        l’application mobile et de l’interface web (suivi des rondes de sécurité, génération de
        rapports, etc.).
      </li>
      <li>
        Communiquer avec les utilisateurs et répondre aux demandes&nbsp;: par exemple, traiter les
        messages envoyés via le formulaire de contact, apporter une assistance technique ou répondre
        aux questions sur le Service.
      </li>
      <li>
        Assurer le bon fonctionnement et la sécurité du Service&nbsp;: par exemple, sauvegarder les
        données, détecter et prévenir les activités malveillantes ou non autorisées, gérer les
        incidents techniques (via l’analyse des journaux/logs) et maintenir l’intégrité des
        systèmes.
      </li>
      <li>
        Améliorer le Service et l’expérience utilisateur&nbsp;: par exemple, analyser de manière
        anonymisée ou agrégée l’usage du site et de l’application (statistiques de fréquentation,
        parcours utilisateur) afin d’identifier des améliorations possibles. Ceci peut inclure
        l’utilisation d’outils d’analyse d’audience tels que Google Analytics.
      </li>
      <li>
        Respecter les obligations légales éventuelles&nbsp;: par exemple, conserver certaines
        données conformément aux exigences légales ou réglementaires, répondre aux demandes des
        autorités publiques ou se conformer à des obligations de sécurité.
      </li>
    </ul>
  </section>

  <section id="base-legale">
    <h2>4. Base légale du traitement</h2>
    <p>
      Selon les finalités exposées ci-dessus, les traitements de données personnelles effectués par
      Qaryos reposent sur les bases juridiques suivantes&nbsp;:
    </p>
    <ul>
      <li>
        <b>Exécution d’un contrat&nbsp;:</b> certaines données sont traitées car elles sont
        nécessaires à l’exécution du contrat liant l’utilisateur à Qaryos. C’est le cas des données
        de compte et d’utilisation du Service, traitées pour fournir les fonctionnalités souscrites
        (<i>RGPD</i> art.&nbsp;6(1)(b)).
      </li>
      <li>
        <b>Intérêt légitime&nbsp;:</b> Qaryos traite des données personnelles pour poursuivre ses
        intérêts légitimes, notamment pour répondre aux demandes de contact spontanées, assurer la
        sécurité et la maintenance du Service, ou améliorer ses services. Dans ces cas, Qaryos
        veille à ce que cet intérêt légitime n’aille pas à l’encontre des droits et libertés de
        l’utilisateur (<i>RGPD</i> art.&nbsp;6(1)(f)).
      </li>
      <li>
        <b>Consentement de l’utilisateur&nbsp;:</b> lorsque la loi l’exige, Qaryos sollicite le
        consentement de l’utilisateur avant de traiter certaines données. Cela concerne par exemple
        l’utilisation de cookies analytiques non essentiels (tels que Google Analytics) sur le site
        web, soumis à l’acceptation de l’utilisateur (<i>RGPD</i> art.&nbsp;6(1)(a)). L’utilisateur
        peut retirer son consentement à tout moment.
      </li>
      <li>
        <b>Obligation légale&nbsp;:</b> enfin, certaines données peuvent être conservées ou
        divulguées afin de respecter une obligation légale à laquelle Qaryos est soumise (<i
          >RGPD</i
        >
        art.&nbsp;6(1)(c)), par exemple en cas d’injonction d’une autorité judiciaire ou pour
        satisfaire aux exigences comptables ou fiscales applicables.
      </li>
    </ul>
  </section>

  <section id="duree-conservation">
    <h2>5. Durée de conservation des données</h2>
    <p>
      Qaryos ne conserve les données personnelles que pour la durée nécessaire aux finalités pour
      lesquelles elles ont été collectées, sous réserve des obligations légales de conservation. À
      titre indicatif&nbsp;:
    </p>
    <ul>
      <li>
        Les données communiquées via le formulaire de contact (identité, coordonnées, message) sont
        conservées le temps de traiter la demande de l’utilisateur puis archivées pendant une durée
        maximale de 1 an, sauf si un suivi commercial ou contractuel justifie une conservation plus
        longue.
      </li>
      <li>
        Les données de compte utilisateur et données liées à l’utilisation du Service sont
        conservées pendant toute la durée d’utilisation active du Service par l’utilisateur, puis
        supprimées ou anonymisées dans un délai raisonnable (par exemple, suppression des comptes
        inactifs). En cas de fermeture du compte ou de fin du contrat, certaines informations
        pourront être archivées en base sécurisée pour une durée supplémentaire (par exemple 5 ans)
        afin de protéger les droits de Qaryos en cas de litige ou de se conformer aux délais de
        prescription légaux.
      </li>
      <li>
        Les journaux de connexion et autres logs techniques sont conservés pour une durée limitée
        (généralement 6 à 12 mois) afin d’assurer la sécurité du site et de l’application (détection
        d’intrusions, prévention des abus, etc.), puis supprimés ou anonymisés.
      </li>
      <li>
        Les données collectées à des fins d’analyse d’audience (ex. cookies Google Analytics) sont
        conservées pour une durée n’excédant pas 13 mois dans un format non agrégé, conformément aux
        recommandations de la CNIL, puis peuvent être conservées plus longtemps sous forme agrégée
        non identifiable à des fins purement statistiques.
      </li>
    </ul>
    <p>
      Au-delà des durées indiquées ci-dessus, certaines données peuvent être conservées plus
      longtemps dans des archives séparées pour répondre à des obligations légales ou
      réglementaires, ou pour la défense des droits en justice, conformément à la législation en
      vigueur.
    </p>
  </section>

  <section id="destinataires">
    <h2>6. Destinataires des données et transferts hors UE</h2>
    <p>
      Les données personnelles collectées sont destinées à Qaryos et aux prestataires autorisés qui
      interviennent dans le cadre du Service. Qaryos veille à ce que chaque destinataire ne puisse
      accéder qu’aux données strictement nécessaires à sa mission et qu’il s’engage à respecter la
      confidentialité et la sécurité des données.
    </p>
    <p>
      Peuvent ainsi avoir accès aux données personnelles, dans la limite de leurs
      attributions&nbsp;:
    </p>
    <ul>
      <li>
        <b>Le personnel habilité de Qaryos&nbsp;:</b> les personnes en charge de l’exploitation du
        Service (par exemple, l’équipe technique, support client ou commercial) peuvent accéder aux
        données dans le respect de leurs fonctions et sont tenues à une obligation de
        confidentialité.
      </li>
      <li>
        <b>Les sous-traitants techniques de Qaryos&nbsp;:</b> des prestataires tiers assurant des
        services pour le compte de Qaryos, par exemple&nbsp;:
        <ul>
          <li>
            <i>Hébergement web&nbsp;:</i> le site internet est hébergé sur les serveurs de la
            société <b>Hostinger</b>, ce qui implique que les données du site (y compris le contenu
            des formulaires de contact) sont stockées sur leur infrastructure sécurisée.
          </li>
          <li>
            <i>Infrastructure applicative&nbsp;:</i> l’application mobile et la base de données du
            Service sont fournies via la plateforme <b>Google Firebase</b> (Google LLC), utilisée
            pour l’authentification des utilisateurs, le stockage des données et la journalisation
            des événements.
          </li>
          <li>
            <i>Analyse d’audience&nbsp;:</i> le site web peut utiliser à l’avenir le service
            <b>Google Analytics</b> (Google LLC) pour collecter des données de fréquentation et de
            navigation à des fins statistiques.
          </li>
        </ul>
        Ces sous-traitants agissent selon les instructions de Qaryos et n’utilisent les données que
        pour les finalités décrites dans la présente Politique. Des contrats de protection des
        données (incluant des Clauses Contractuelles Types de la Commission européenne) ont été
        conclus avec ces prestataires pour encadrer le traitement et assurer un niveau de protection
        conforme au RGPD.
      </li>
      <li>
        <b>Les autorités légalement habilitées&nbsp;:</b> Qaryos peut être amené à divulguer
        certaines données personnelles à des organismes publics, des autorités judiciaires,
        officiers ministériels ou tiers autorisés lorsque la loi l’exige, ou pour exercer ou
        défendre des droits en justice.
      </li>
    </ul>
    <p>
      Qaryos ne vend ni ne cède en aucun cas les données personnelles de ses utilisateurs à des
      tiers à des fins commerciales. Tout transfert de données à un tiers (hors cadre sous-traitance
      ou obligation légale) ne pourrait avoir lieu qu’avec le consentement explicite de
      l’utilisateur.
    </p>
    <p>
      <b>Transferts hors de l’Union Européenne&nbsp;:</b> certains des sous-traitants mentionnés
      ci-dessus sont des sociétés internationales susceptibles de traiter les données en dehors de
      l’EEE (par exemple, aux États-Unis). En particulier, les services de Google (Firebase,
      Analytics) sont fournis par une entité américaine (Google LLC) pouvant impliquer un
      hébergement ou un accès aux données depuis les États-Unis. Dans ce cas, Qaryos s’assure que
      des garanties appropriées sont en place pour encadrer ces transferts, telles que l’adhésion du
      prestataire à un accord de protection des données reconnu (p.ex. le dispositif
      <i>EU-U.S. Data Privacy Framework</i> si applicable) ou la mise en œuvre des Clauses
      Contractuelles Types approuvées par la Commission européenne, assorties de mesures de sécurité
      complémentaires (chiffrement, etc.). L’utilisateur peut contacter Qaryos pour obtenir
      davantage d’informations sur ces garanties.
    </p>
  </section>

  <section id="securite">
    <h2>7. Sécurité des données</h2>
    <p>
      Qaryos met en œuvre des mesures de sécurité techniques et organisationnelles conformes aux
      standards de l’industrie pour protéger les données personnelles contre toute perte, accès non
      autorisé, divulgation ou altération. Ces mesures comprennent notamment le chiffrement des
      communications (certificat SSL/TLS sur le site web), le stockage sécurisé des données sur des
      serveurs protégés (ceux de Hostinger et Firebase), la gestion des droits d’accès aux données,
      et des procédures de sauvegarde régulières.<br />
      Bien que Qaryos s’efforce de protéger au mieux les informations, il convient de noter
      qu’aucune mesure de sécurité n’est infaillible. Par conséquent, la sécurité absolue ne peut
      être garantie. L’utilisateur est également responsable de la confidentialité de ses
      identifiants de connexion et doit veiller à utiliser des mots de passe robustes et à les
      garder secrets.
    </p>
    <p>
      En cas de violation de données personnelles (accès non autorisé, perte, etc.), Qaryos s’engage
      à respecter les obligations légales en vigueur, notamment à notifier la violation à la CNIL
      et/ou aux personnes concernées lorsque la loi l’exige (dans les meilleurs délais et si
      possible dans les 72 heures).
    </p>
  </section>

  <section id="droits-utilisateurs">
    <h2>8. Droits des utilisateurs</h2>
    <p>
      Conformément au RGPD et à la loi Informatique et Libertés, chaque utilisateur dispose des
      droits suivants concernant ses données personnelles&nbsp;:
    </p>
    <ul>
      <li>
        <b>Droit d’accès&nbsp;:</b> l’utilisateur peut obtenir la confirmation que ses données
        personnelles sont ou ne sont pas traitées par Qaryos, et le cas échéant, accéder auxdites
        données et en recevoir une copie, ainsi que des informations sur les finalités du
        traitement.
      </li>
      <li>
        <b>Droit de rectification&nbsp;:</b> l’utilisateur peut demander la correction des données
        personnelles inexactes ou incomplètes le concernant, afin d’assurer que les informations
        soient exactes et à jour.
      </li>
      <li>
        <b>Droit d’effacement&nbsp;:</b> également connu sous le nom de « droit à l’oubli », ce
        droit permet à l’utilisateur de demander la suppression de ses données personnelles dans
        certains cas (par exemple, si elles ne sont plus nécessaires au regard des finalités ou si
        l’utilisateur retire son consentement à un traitement facultatif).
      </li>
      <li>
        <b>Droit d’opposition&nbsp;:</b> l’utilisateur peut, pour des raisons tenant à sa situation
        particulière, s’opposer à tout moment au traitement de ses données personnelles fondé sur
        l’intérêt légitime de Qaryos. Il peut également s’opposer à ce que ses données soient
        utilisées à des fins de prospection commerciale.
      </li>
      <li>
        <b>Droit à la limitation du traitement&nbsp;:</b> dans certains cas définis par la
        réglementation (par exemple si l’utilisateur conteste l’exactitude de ses données ou la
        licéité d’un traitement), l’utilisateur peut demander la suspension temporaire du traitement
        de ses données (les données ne sont alors que conservées, sans autre usage).
      </li>
      <li>
        <b>Droit à la portabilité&nbsp;:</b> pour les données que l’utilisateur a fournies à Qaryos
        et qui sont traitées de manière automatisée sur la base de son consentement ou d’un contrat,
        l’utilisateur dispose du droit de recevoir ces données dans un format structuré, couramment
        utilisé et lisible par machine, ou de demander à Qaryos de les transmettre à un autre
        responsable de traitement si cela est techniquement possible.
      </li>
      <li>
        <b>Droit de retirer son consentement&nbsp;:</b> pour les traitements fondés sur le
        consentement de l’utilisateur (par exemple l’utilisation de cookies analytiques),
        l’utilisateur a le droit de retirer son consentement à tout moment. Le retrait du
        consentement ne remet pas en cause la licéité du traitement effectué avant ce retrait.
      </li>
      <li>
        <b>Droit de définir des directives post-mortem&nbsp;:</b> conformément à l’article 85 de la
        loi Informatique et Libertés, l’utilisateur a la possibilité de formuler des directives
        relatives à la conservation, à l’effacement et à la communication de ses données
        personnelles après son décès.
      </li>
      <li>
        <b>Droit d’introduire une réclamation&nbsp;:</b> si l’utilisateur estime que ses droits ne
        sont pas respectés, il peut introduire une plainte auprès de l’autorité de contrôle
        compétente de son pays de résidence (en France, la <i>CNIL</i>).
      </li>
    </ul>
    <p>
      Ces droits peuvent être exercés à tout moment en adressant une demande à Qaryos (par e-mail à
      <strong>contact&#64;Qaryos.fr</strong> ou par courrier postal aux coordonnées figurant dans
      les Mentions Légales). Qaryos pourra demander un justificatif d’identité si nécessaire afin de
      vérifier l’identité du demandeur. Une réponse sera apportée dans les meilleurs délais et en
      tout état de cause dans le délai d’un mois prévu par le RGPD (prolongeable de deux mois compte
      tenu de la complexité et du nombre de demandes le cas échéant).
    </p>
  </section>

  <section id="cookies">
    <h2>9. Cookies et outils de suivi</h2>
    <p>
      Lors de la consultation du site <i>www.Qaryos.fr</i>, des cookies et technologies de suivi
      peuvent être déposés sur le terminal de l’utilisateur (ordinateur, smartphone, etc.). Un
      cookie est un petit fichier texte qui enregistre des informations relatives à la navigation de
      l’utilisateur sur le site, sans toutefois permettre de l’identifier directement.
    </p>
    <p>
      Qaryos utilise principalement des cookies pour assurer le fonctionnement technique du site et
      de l’interface de connexion (par exemple, gestion de la session utilisateur lors de la
      connexion à l’espace administrateur). Ces cookies techniques, nécessaires au fonctionnement du
      Service, ne requièrent pas le consentement préalable de l’utilisateur.
    </p>
    <p>
      En outre, Qaryos pourrait utiliser prochainement des cookies ou traceurs à des fins d’analyse
      d’audience et de performance, notamment via Google Analytics. Ces cookies analytiques (non
      essentiels) permettent de collecter des informations sur la façon dont les visiteurs utilisent
      le site (par exemple, pages les plus visitées, durée de navigation, type d’appareil utilisé,
      ville de connexion approximative). Les données recueillies servent à améliorer le Service et
      sont exploitées de manière agrégée. Si mis en place, ces outils seront configurés de manière à
      anonymiser les données collectées lorsque cela est possible (par exemple, anonymisation des
      adresses IP).
    </p>
    <p>
      Conformément à la réglementation, l’utilisation de cookies analytiques par Qaryos sera soumise
      au consentement de l’utilisateur. Lors de sa première visite sur le site, l’utilisateur sera
      invité, via un bandeau cookies, à accepter ou refuser ce type de cookies. L’utilisateur peut
      également paramétrer son navigateur pour bloquer ou supprimer les cookies existants. Il est à
      noter qu’un refus des cookies analytiques n’affectera pas le fonctionnement du Service.
    </p>
    <p>
      Pour plus d’informations sur l’utilisation des cookies par Qaryos, l’utilisateur pourra se
      référer à la documentation fournie dans le bandeau cookies ou contacter Qaryos à l’adresse
      indiquée ci-dessus.
    </p>
  </section>

  <section id="evolution">
    <h2>10. Évolution de la politique de confidentialité</h2>
    <p>
      Qaryos se réserve le droit de modifier la présente Politique de Confidentialité à tout moment,
      notamment pour refléter les évolutions du Service, les retours des utilisateurs ou pour se
      conformer à d’éventuels changements légaux ou réglementaires.<br />
      En cas de modification substantielle affectant les droits des utilisateurs, Qaryos s’engage à
      en informer les utilisateurs avec un préavis raisonnable, par exemple via une notification sur
      le site ou par e-mail. Dans tous les cas, la version mise à jour de la politique, indiquant la
      date de « dernière mise à jour », sera publiée sur le site. Il est conseillé aux utilisateurs
      de la consulter régulièrement.
    </p>
    <p>
      L’utilisation du Service après la date d’effet de ces modifications vaudra reconnaissance et
      acceptation de la nouvelle version de la Politique de Confidentialité.
    </p>
  </section>
</div>
